import { useUnit } from 'effector-react';
import _ from 'lodash';
import { $isDesktop } from 'models/device-params-model';
import $$dropdowns from 'models/dropdowns';
import $$ep from 'models/ep';
import { $isAdvisorPanel, $isSignupPage } from 'models/location';
import $$trustFunding from 'models/pages/trust-funding';
import { $global, storage } from 'models/store-global';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Frame, Link, NotificationsMark } from 'components/StyledTemplates/StyledTemplates';
import {
    MenuBlock,
    MenuBlockHeader,
    MenuBlockLinkHeader,
    MenuItem,
} from 'components/pages/Dashboard/common/CommunityPageStyles';

import { isna, togglePush } from 'utils/common-helper';

import useExecutionSteps from 'hooks/useExecutionSteps';
import useResources from 'hooks/useResources';

import imgIconsDashboardSidebarCommunity from 'assets/icons/dashboard-sidebar-community.svg';
import imgIconsDashboardSidebarEstatePlan from 'assets/icons/dashboard-sidebar-estate-plan.svg';
import imgIconsDashboardSidebarLearn from 'assets/icons/dashboard-sidebar-learn.svg';
import imgIconsDashboardSidebarRoles from 'assets/icons/dashboard-sidebar-roles.svg';
import imgIconsDashboardSidebarTrustFunding from 'assets/icons/dashboard-sidebar-trust-funding.svg';

import { MyStore } from 'types/Store';
import { Document } from 'types/zod/document';

type MenuItem = {
    type: 'header' | 'item' | 'linkHeader';
    label: string;
    link?: string;
    icon?: string;
    visible?: boolean;
    counter?: number;
    absolute?: boolean;
    dataQaId?: string;
};

const Menu = () => {
    const { t } = useResources(['common', 'assessment']);

    const { pathname } = useLocation();
    const isTrustFundingPageAvailable = useUnit($$trustFunding.$pageIsAvailable);
    const isAdvisorPanel = useUnit($isAdvisorPanel);
    const isSignupPage = useUnit($isSignupPage);
    const path = pathname.split('/').slice(1);

    const isDesktop = useUnit($isDesktop);

    const { steps: remainingSteps, loaded: remainingStepsLoaded } = useExecutionSteps();
    const opened = useUnit($$dropdowns.$opened.map((opened) => opened.includes('profileContextMenu')));

    const sourceEstatePlan = useUnit($$ep.stores.$epQInstance);
    // To display a fake non-clickable background menu.
    const estatePlan = isSignupPage ? ({ status: 'Complete' } as const) : sourceEstatePlan;
    const isInProgress = !!estatePlan && estatePlan.status === 'In_Progress';
    const isComplete = !!estatePlan && estatePlan.status === 'Complete';

    const assigned = useUnit($global.map((state) => state.community?.assigned ?? []));

    const selectedParticipantId = useUnit($global.map((state) => state.temp?.selected_community_participant));

    const docs = useUnit($global.map((state) => state.community?.docs || []));
    const visibleDocuments = docs.filter(
        (document: Document) =>
            selectedParticipantId &&
            document?.ownership?.includes?.(selectedParticipantId) &&
            ['estate_plan', 'supporting'].includes(document.category_code),
    );

    const openedMobileMenuItems = useUnit($global.map((state) => state.temp?.openedMobileMenuItems || []));
    const setOpenedMobileMenuItems = (newValue: string) => {
        storage.set(
            (store): MyStore => ({
                ...store,
                temp: { ...(store.temp || {}), openedMobileMenuItems: togglePush(openedMobileMenuItems, newValue) },
            }),
        );
    };

    useEffect(() => {
        const defaultOpenedItems = [t('newDashboardPage.menuB1Header'), t('newDashboardPage.menuB2Header')];
        if (opened) {
            storage.set(
                (store): MyStore => ({
                    ...store,
                    temp: {
                        ...(store.temp || {}),
                        openedMobileMenuItems: _.uniq([...openedMobileMenuItems, ...defaultOpenedItems]),
                    },
                }),
            );
        }
    }, [opened]);

    const menu: MenuItem[] = [
        {
            type: 'header',
            label: t('newDashboardPage.menuB1Header'),
            icon: imgIconsDashboardSidebarEstatePlan,
            visible: !isAdvisorPanel,
            dataQaId: 'orig_menu_ep',
        },
        {
            type: 'item',
            label: t('newDashboardPage.menuItemInitial'),
            visible: !estatePlan?.status,
            link: '/dashboard/initial',
        },
        {
            type: 'item',
            label: t('newDashboardPage.menuItemInProgress'),
            link: '/dashboard/in-progress',
            visible: !isAdvisorPanel && isInProgress,
            dataQaId: 'orig_menu_in_progress',
        },
        {
            type: 'item',
            label: t('newDashboardPage.menuItemCompleteDashboard'),
            counter: +(remainingStepsLoaded && remainingSteps?.filter?.((i) => !i?.done)?.length),
            link: '/dashboard/complete',
            visible: !isAdvisorPanel && isComplete,
            dataQaId: 'orig_menu_complete_dashboard',
        },
        {
            type: 'item',
            label: t('newDashboardPage.menuItemDocuments'),
            link: '/dashboard/documents',
            visible: !isAdvisorPanel && !!visibleDocuments?.length,
            dataQaId: 'orig_menu_documents',
        },
        {
            type: 'linkHeader',
            label: t('newDashboardPage.menuItemGiftTrust'),
            icon: imgIconsDashboardSidebarEstatePlan,
            link: '/dashboard/gift-trust',
            dataQaId: 'orig_menu_gift_trust',
            visible: t('features.giftTrustMenuItem') === true,
        },
        {
            type: 'header',
            label: t('newDashboardPage.menuB2Header'),
            icon: imgIconsDashboardSidebarCommunity,
            visible: !isAdvisorPanel && !isna(estatePlan?.status),
            dataQaId: 'orig_menu_community',
        },
        {
            type: 'item',
            label: t('newDashboardPage.menuItemAdvocates'),
            link: '/community/advocates',
            visible: !isAdvisorPanel && !isna(estatePlan?.status),
            dataQaId: 'orig_menu_advocates',
        },
        {
            type: 'item',
            label: t('newDashboardPage.menuItemBeneficiaries'),
            link: '/community/beneficiaries',
            visible: !isAdvisorPanel && !isna(estatePlan?.status),
            dataQaId: 'orig_menu_beneficiaries',
        },
        {
            type: 'item',
            label: t('newDashboardPage.inviteAdvisorHeader'),
            link: '/community/observers',
            absolute: false,
            visible: !isAdvisorPanel && !isna(estatePlan?.status),
            dataQaId: 'orig_menu_observers',
        },
        {
            type: 'linkHeader',
            label: t('newDashboardPage.menuHeaderTrustFunding'),
            link: '/dashboard/trust-funding',
            icon: imgIconsDashboardSidebarTrustFunding,
            visible: isTrustFundingPageAvailable,
            dataQaId: 'orig_menu_trust_funding',
        },
        {
            type: 'header',
            label: t('newDashboardPage.menuB3Header'),
            icon: imgIconsDashboardSidebarRoles,
            visible: !isAdvisorPanel && assigned?.length > 0,
            dataQaId: 'orig_menu_roles',
        },
        ...assigned.map(
            (item): MenuItem => ({
                type: 'item',
                label: item.full_name ?? '',
                link: `/community/assigned/${item.participant_id}`,
                visible: !isAdvisorPanel,
                dataQaId: `orig_menu_roles_${item.participant_id}`,
            }),
        ),
        {
            type: 'linkHeader',
            label: t('newDashboardPage.learnHeader'),
            icon: imgIconsDashboardSidebarLearn,
            link: 'https://www.myadvocate.com/learn',
            absolute: true,
            visible: !isAdvisorPanel,
            dataQaId: 'orig_menu_learn',
        },
    ]
        .filter((i): i is MenuItem => i.visible !== false)
        .map((item, index, self) => {
            if (item.type === 'header') {
                return { ...item, type: 'linkHeader', link: self[index + 1]?.link };
            }
            return item;
        });

    return (
        <MenuBlock>
            {menu.map((item, index, self) => {
                const previousHeader = menu
                    .slice(0, index)
                    .reverse()
                    .find((item) => ['header', 'linkHeader'].some((i) => i === item.type))?.label;

                const hasChildrenItems =
                    index < self.length - 1 &&
                    item.type.toLowerCase().includes('header') &&
                    !self[index + 1].type.toLowerCase().includes('header');

                return {
                    header: (
                        <MenuBlockHeader
                            data-qaid={item.dataQaId}
                            key={index}
                            icon={item.icon}
                            $opened={openedMobileMenuItems?.includes?.(item.label)}
                            onClick={() => {
                                setOpenedMobileMenuItems(item.label);
                            }}
                        >
                            {item.label}
                        </MenuBlockHeader>
                    ),
                    linkHeader: (
                        <MenuBlockLinkHeader
                            data-qaid={item.dataQaId}
                            key={index}
                            as={item.link && (isDesktop || !hasChildrenItems) ? Link : Frame}
                            to={item.link}
                            icon={item.icon}
                            $opened={openedMobileMenuItems?.includes?.(item.label)}
                            onClick={() => {
                                setOpenedMobileMenuItems(item.label);
                            }}
                            absolute={item.absolute}
                            $hideArrow={true}
                        >
                            {item.label}
                        </MenuBlockLinkHeader>
                    ),
                    item: (
                        <MenuItem
                            data-qaid={item.dataQaId}
                            key={index}
                            as={item.link ? Link : Frame}
                            to={item.link}
                            $openedOnMobile={!!previousHeader && openedMobileMenuItems?.includes?.(previousHeader)}
                            selected={item.link?.split?.('/')?.slice?.(1)?.join?.('/') === path.join('/')}
                        >
                            {item.label}
                            {(item?.counter || 0) > 0 && estatePlan && 'questionnaire_instance_id' in estatePlan && (
                                <NotificationsMark background="red" count={item.counter} />
                            )}
                        </MenuItem>
                    ),
                }[item.type];
            })}
        </MenuBlock>
    );
};

export default Menu;
